<script setup lang="ts">
  import { ProgressBar } from '@/entities/Flow'
  import FlowHeader from '@/entities/Flow/components/FlowHeader/FlowHeader.vue'
  import FlowHeaderQC from '@/entities/Flow/components/FlowHeaderQC/FlowHeaderQC.vue'
  import FlowNeedHelpWidgetQC from '@/widgets/Flow/FlowNeedHelpWidgetQC/FlowNeedHelpWidgetQC.vue'
  import { BACK_EVENT_NAME } from './FlowWidget.constants'
  import {
    DEFAULT_FLOW_STEPS_COUNT,
    DEFAULT_FLOW_CURRENT_STEP,
    DEFAULT_FLOW_FIRST_STEP,
  } from '@/shared/constants/flow.constants'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import { computed } from 'vue'
  import { useFeaturesStore } from '@/shared/store/featuresStore'
  interface Props {
    href?: string
    withArrowBack?: boolean
    isRtl?: boolean
    stepsCount?: number
    currentStep?: number
    firstStep?: number
    logoAffinity?: string
    withProgressBar?: boolean
  }

  interface Emits {
    (e: typeof BACK_EVENT_NAME): void
  }

  const props = withDefaults(defineProps<Props>(), {
    withArrowBack: true,
    isRtl: false,
    href: undefined,
    stepsCount: DEFAULT_FLOW_STEPS_COUNT,
    currentStep: DEFAULT_FLOW_CURRENT_STEP,
    firstStep: DEFAULT_FLOW_FIRST_STEP,
    withProgressBar: true,
    logoAffinity: undefined,
  })

  const emit = defineEmits<Emits>()

  const currentStep = computed<number>({
    get: () => props.currentStep ?? 0,
    set: (value: number) => (currentStep.value = value ?? 0),
  })

  const featuresStore = useFeaturesStore()
  const isProgressBarQcEnabled = computed(() => featuresStore.isProgressBarQcEnabled)
</script>

<template>
  <div class="flow-widget">
    <div id="download-banner-container" />
    <div class="flow-widget__content">
      <FlowHeaderQC
        v-if="isProgressBarQcEnabled"
        v-model:current-step="currentStep"
        :with-progress-bar="currentStep > 0 && withProgressBar"
        data-test="flow-header-qc"
      />
      <WrapperQC v-else direction="column" gap="var(--spacing-5)">
        <FlowHeader
          :with-arrow-back="withArrowBack"
          :rtl="isRtl"
          :href="href"
          @go-back="emit('goBack')"
        />
        <ProgressBar
          v-if="(stepsCount ?? 0) > 0"
          class="flow-widget__progress"
          :steps-count="stepsCount"
          :current-step="currentStep"
          :first-step="firstStep"
        />
      </WrapperQC>

      <slot />

      <div class="flow-widget__footer">
        <div class="flow-widget__footer-inner">
          <template v-if="logoAffinity">
            <div class="flow-widget__affinity">
              <img :src="`data:image/png;base64,${logoAffinity}`" />
            </div>
          </template>
          <FlowNeedHelpWidgetQC />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import './FlowWidget';

  .ds-flow-widget {
    background-color: var(--colorBgSrfSecondary);
  }
</style>
